/**
 * Service status: the machinery every tenant shares (PLAN-2026-09-15 C#7).
 *
 * One row per component that reports into `component_health` — the collector,
 * the disk, the nightly rollup and prune, the reconcile, the coverage ledger,
 * the telemetry subscriber. A component that has stopped reporting reads
 * `unknown` with how long it has been quiet, because a job that died writes
 * nothing, and a registry that only showed what was written would show the
 * last green row forever (legacy's thirteen-night backup outage).
 */

import { useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, DataTable, Empty, Loading, Pill, Section, type ColumnDef, type PillState } from './components/index.ts';

interface Component {
  component: string;
  label: string;
  status: 'ok' | 'degraded' | 'failed' | 'unknown';
  reportedStatus: 'ok' | 'degraded' | 'failed';
  detail: string | null;
  stale: boolean;
  secondsSinceReport: number;
  everySeconds: number;
  changedAt: string;
}

const STATE: Record<Component['status'], PillState> = { ok: 'ok', degraded: 'warning', failed: 'fault', unknown: 'stale' };

function ago(seconds: number): string {
  if (seconds < 90) return `${seconds}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172_800) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}

const columns: ColumnDef<Component, unknown>[] = [
  {
    id: 'status',
    header: 'Status',
    accessorFn: (c) => ['failed', 'unknown', 'degraded', 'ok'].indexOf(c.status),
    cell: ({ row }) => (
      <Pill
        state={STATE[row.original.status]}
        reason={row.original.stale ? `Last said ${row.original.reportedStatus}, then went quiet.` : null}
      >
        {row.original.status === 'unknown' ? 'quiet' : row.original.status}
      </Pill>
    ),
    size: 110,
  },
  {
    id: 'label',
    header: 'Component',
    accessorKey: 'label',
    cell: ({ row }) => (
      <div>
        <div className="cell-strong">{row.original.label}</div>
        <div className="small muted mono">{row.original.component}</div>
      </div>
    ),
  },
  {
    id: 'detail',
    header: 'Last said',
    accessorKey: 'detail',
    enableSorting: false,
    cell: ({ row }) => <span className="small muted">{row.original.detail ?? '—'}</span>,
  },
  {
    id: 'reported',
    header: 'Reported',
    accessorKey: 'secondsSinceReport',
    cell: ({ row }) => (
      <span className="small">
        {ago(row.original.secondsSinceReport)}
        <span className="muted"> · expected every {ago(row.original.everySeconds).replace(' ago', '')}</span>
      </span>
    ),
    size: 220,
  },
];

export function ServiceStatus(): React.JSX.Element {
  const [components, setComponents] = useState<Component[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<{ components: Component[] }>('/api/platform/health')
      .then((r) => live && setComponents(r.components))
      .catch((err: unknown) => live && setError(err instanceof ApiError ? err.message : String(err)));
    return (): void => {
      live = false;
    };
  }, []);

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (components === null) return <Loading rows={4} cols={4} label="Loading service status" />;

  const bad = components.filter((c) => c.status !== 'ok');
  return (
    <Section
      flush
      title="Service status"
      summary={
        components.length === 0
          ? undefined
          : bad.length === 0
            ? `All ${components.length} components reporting healthy`
            : `${bad.length} of ${components.length} need attention`
      }
    >
      <DataTable
        data={components}
        columns={columns}
        getRowId={(c) => c.component}
        initialSort={[{ id: 'status', desc: false }]}
        rowClass={(c) => (c.status === 'failed' ? 'row-fault' : undefined)}
        caption="Service status"
        empty={<Empty title="No component has ever reported" body="The registry is installed and nothing writes to it yet, which is not the same as healthy." />}
      />
    </Section>
  );
}
